import React from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/navigation';
import { Footer } from '../components/footer';
import { Award, Target, Users, Wrench } from 'lucide-react';
import { useLanguage } from '../context/languagecontext';

const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Award, title: t('about.value1.title'), description: t('about.value1.description') },
    { icon: Target, title: t('about.value2.title'), description: t('about.value2.description') },
    { icon: Users, title: t('about.value3.title'), description: t('about.value3.description') },
    { icon: Wrench, title: t('about.value4.title'), description: t('about.value4.description') },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="relative bg-slate-900 text-white py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/images/photo_5116493174661647889_y.jpg"
            alt="About"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-5xl md:text-6xl font-bold mb-6 uppercase tracking-wide"
          >
            {t('about.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-body text-xl text-gray-300 max-w-3xl mx-auto"
          >
            {t('about.subtitle')}
          </motion.p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6 uppercase tracking-wide">
                {t('about.heading')}
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-6">
                {t('about.paragraph1')}
              </p>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-6">
                {t('about.paragraph2')}
              </p>
              <p className="font-body text-lg text-gray-600 leading-relaxed">
                {t('about.paragraph3')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="rounded-sm overflow-hidden shadow-2xl border-2 border-slate-200">
                <img
                  src="/images/photo_5116493174661647893_y.jpg"
                  alt="Our work"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white rounded-sm p-6 shadow-hard">
                <div className="font-heading text-4xl font-bold">5+</div>
                <div className="font-body text-sm uppercase tracking-wider mt-1">Years Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-wide">
              {t('about.coreValues')}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white border border-slate-200 hover:border-orange-500 rounded-sm p-8 transition-all group shadow-md hover:shadow-hard"
              >
                <div className="w-16 h-16 bg-orange-500 rounded-sm flex items-center justify-center mb-6 group-hover:shadow-hard-sm transition-all">
                  <value.icon className="w-8 h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3 uppercase tracking-wider">
                  {value.title}
                </h3>
                <p className="font-body text-gray-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
